import React, { Dispatch, SetStateAction, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AdditionalDocuments } from '@/features/tasks/types/additionalDocuments';
import { Alert, Box, Button, Divider, Drawer, Typography } from '@mui/material';
import { RootState } from '../../../../app/rootReducer';

import { AppDispatch } from '../../../../app/store';
import { NoraButton, Spacer } from '../../../../common/components/v2';
import { Resource } from '../../../../common/constants/resource';
import { FeatureFlagsContext } from '../../../../common/featureFlagsWrapper';
import { isEmpty } from '../../../../common/helpers';
import { EvidenceOrderProfile } from '../../../../common/productConfig/productConfig';
import { ProductFactory } from '../../../../common/productConfig/productFactory';
import { EvidenceOrder } from '../../../tasks/slice/evidenceOrder';
import { placeOrder } from '../../../tasks/slice/taskThunks';
import { AdditionalCustomerInfo } from '../../../tasks/types/additionalCustomerInfo';
import { Aps } from '../../../tasks/types/aps';
import { AutoMedicalClaims } from '../../../tasks/types/autoMedicalClaims';
import { CriminalRecords } from '../../../tasks/types/criminalRecords';
import { Dispute } from '../../../tasks/types/dispute';
import { EHR } from '../../../tasks/types/ehr';
import { Eti } from '../../../tasks/types/eti';
import { InsuranceLabs } from '../../../tasks/types/insuranceLabs';
import { LabPiqture } from '../../../tasks/types/labPiqture';
import { MedExam } from '../../../tasks/types/medExam';
import { MedicalClaims } from '../../../tasks/types/medicalClaims';
import { MibCodeDetails } from '../../../tasks/types/mibCodeDetails';
import { PlanF } from '../../../tasks/types/planF';
import { ScriptCheck } from '../../../tasks/types/scriptCheck';
import { SpecialAuthorization } from '../../../tasks/types/specialAuthorization';
import { TaskTypes } from '../../../tasks/types/taskTypes';
import { TRLOrder } from '../../../tasks/types/TRLOrder';
import styles from '../styles.module.scss';
import { APSSummary } from './aps/APSSummary';
import { DisputeSummary } from './dispute/DisputeSummary';
import { EHRSummary } from './ehr/EHRSummary';
import { ETISummary } from './eti/ETISummary';
import { InsuranceLabsSummary } from './insuranceLabs/InsuranceLabsSummary';
import { MedExamSummary } from './medExam/MedExamSummary';
import { MibCodeDetailsSummary } from './mibCodeDetails/MibCodeDetailsSummary';
import { SimpleSummary } from './simple/SimpleSummary';

const SUBMIT_BUTTON_NAME = 'evidence-drawer-submit-button';

export const ReviewEvidence = ({
  applicationId,
  isOpen,
  setIsOpen,
  setIsReview,
  medicalExam,
  aps,
  eti,
  ehr,
  labPiqture,
  medicalClaims,
  scriptCheck,
  mibCodeDetails,
  insuranceLabs,
  criminalRecords,
  additionalCustomerInfo,
  specialAuthorization,
  planF,
  autoMedicalClaims,
  trlOrder,
  dispute,
  additionalDocuments,
  openSnacks,
  updateSnacks,
}: {
  applicationId: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsReview: Dispatch<SetStateAction<boolean>>;
  medicalExam: MedExam;
  aps: Aps;
  eti: Eti;
  ehr: EHR;
  labPiqture: LabPiqture;
  medicalClaims: MedicalClaims;
  scriptCheck: ScriptCheck;
  mibCodeDetails: MibCodeDetails;
  insuranceLabs: InsuranceLabs;
  criminalRecords: CriminalRecords;
  additionalCustomerInfo: AdditionalCustomerInfo;
  specialAuthorization: SpecialAuthorization;
  planF: PlanF;
  autoMedicalClaims: AutoMedicalClaims;
  trlOrder: TRLOrder;
  dispute: Dispute;
  openSnacks: any;
  updateSnacks: (updatedSnacks: any) => void;
  additionalDocuments: AdditionalDocuments;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { flags } = useContext(FeatureFlagsContext);
  const { isApsDoctorInfoEnabled } = flags;

  const subCategory = useSelector(
    (state: RootState) => state.application.subCategory,
  );
  const product = useSelector((state: RootState) => state.application.product);
  const carrier = useSelector((state: RootState) => state.application.carrier);
  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );
  const showsInsuredEvidenceOrderNotice =
    !!productConfig &&
    productConfig.getEvidenceOrderProfile() === EvidenceOrderProfile.INSURED;

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      variant="persistent"
      classes={{ paper: styles.Drawer }}
    >
      <Box mt={6}>
        <Box
          m={3}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1">Order Evidences</Typography>
          <Button onClick={() => setIsReview(false)}>
            <Typography variant="subtitle2" color="textSecondary">
              Back
            </Typography>
          </Button>
        </Box>
        <Divider />
        {showsInsuredEvidenceOrderNotice && (
          <Box mx={3} mt={2}>
            <Alert severity="info">
              Evidence will be ordered for the Insured profile.
            </Alert>
          </Box>
        )}
        <Box m={3}>
          {!isEmpty(medicalExam) && (
            <>
              <MedExamSummary medExam={medicalExam} />
              <Spacer height={32} />
            </>
          )}
          {!isEmpty(eti) && (
            <>
              <ETISummary eti={eti} />
              <Spacer height={32} />
            </>
          )}
          {!isEmpty(aps) && (
            <>
              <APSSummary aps={aps} />
              <Spacer height={32} />
            </>
          )}
          {!isEmpty(dispute) && (
            <>
              <DisputeSummary dispute={dispute} />
              <Spacer height={32} />
            </>
          )}
          {!isEmpty(ehr) && <EHRSummary data={ehr} />}
          {!isEmpty(labPiqture) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_LAB_PIQTURE}
              description={Resource.TASK_DESCRIPTION_LAB_PIQTURE}
              data={labPiqture}
            />
          )}
          {!isEmpty(autoMedicalClaims) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_AUTO_MEDICAL_CLAIMS}
              description={Resource.TASK_DESCRIPTION_AUTO_MEDICAL_CLAIMS}
              data={autoMedicalClaims}
            />
          )}
          {!isEmpty(trlOrder) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_TRL}
              description={Resource.TASK_DESCRIPTION_TRL}
              data={trlOrder}
            />
          )}
          {!isEmpty(medicalClaims) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_MEDICAL_CLAIMS}
              description={Resource.TASK_DESCRIPTION_MEDICAL_CLAIMS}
              data={medicalClaims}
            />
          )}
          {!isEmpty(scriptCheck) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_SCRIPT_CHECK}
              description={Resource.TASK_DESCRIPTION_SCRIPT_CHECK}
              data={scriptCheck}
            />
          )}
          {!isEmpty(mibCodeDetails) && (
            <MibCodeDetailsSummary
              title={Resource.TASK_TITLE_MIB_CODE_DETAILS}
              data={mibCodeDetails}
            />
          )}
          {!isEmpty(insuranceLabs) && (
            <InsuranceLabsSummary
              title={Resource.TASK_TITLE_INSURANCE_LABS}
              description={Resource.TASK_DESCRIPTION_INSURANCE_LABS}
              data={insuranceLabs}
            />
          )}
          {!isEmpty(criminalRecords) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_CRIMINAL_RECORDS}
              description={Resource.TASK_DESCRIPTION_CRIMINAL_RECORDS}
              data={criminalRecords}
            />
          )}
          {!isEmpty(additionalCustomerInfo) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_ADDITIONAL_CUSTOMER_INFO}
              description={Resource.TASK_DESCRIPTION_ADDITIONAL_CUSTOMER_INFO}
              data={additionalCustomerInfo}
            />
          )}
          {!isEmpty(specialAuthorization) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_SPECIAL_AUTHORIZATION}
              description={Resource.TASK_DESCRIPTION_SPECIAL_AUTHORIZATION}
              data={specialAuthorization}
            />
          )}
          {!isEmpty(planF) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_PLAN_F}
              description={Resource.TASK_DESCRIPTION_PLAN_F}
              data={planF}
            />
          )}
          {!isEmpty(additionalDocuments) && (
            <SimpleSummary
              title={Resource.TASK_TITLE_ADDITIONAL_DOCUMENTS}
              description={Resource.TASK_DESCRIPTION_ADDITIONAL_DOCUMENTS}
              data={additionalDocuments}
            />
          )}
        </Box>
        <Box>
          <NoraButton
            name={SUBMIT_BUTTON_NAME}
            className={styles.DrawerButton}
            onClick={() => {
              const orders: EvidenceOrder[] = [] as EvidenceOrder[];
              if (!isEmpty(medicalExam)) {
                const medExamOrder: EvidenceOrder = new EvidenceOrder(
                  TaskTypes.MEDEXAM,
                  medicalExam,
                );
                orders.push(medExamOrder);
              }

              if (!isEmpty(aps)) {
                const data = isApsDoctorInfoEnabled
                  ? { ...aps, doctorInfo: '' }
                  : aps;
                const apsOrder: EvidenceOrder = new EvidenceOrder(
                  TaskTypes.APS,
                  data,
                );
                orders.push(apsOrder);
              }

              if (!isEmpty(eti)) {
                const etiOrder: EvidenceOrder = new EvidenceOrder(
                  TaskTypes.ETI,
                  eti,
                );
                orders.push(etiOrder);
              }

              if (!isEmpty(ehr)) {
                orders.push(new EvidenceOrder(TaskTypes.EHR, ehr));
              }

              if (!isEmpty(labPiqture)) {
                orders.push(
                  new EvidenceOrder(TaskTypes.LAB_PIQTURE, labPiqture),
                );
              }
              if (!isEmpty(medicalClaims)) {
                orders.push(
                  new EvidenceOrder(TaskTypes.MEDICAL_CLAIMS, medicalClaims),
                );
              }
              if (!isEmpty(scriptCheck)) {
                orders.push(
                  new EvidenceOrder(TaskTypes.SCRIPT_CHECK, scriptCheck),
                );
              }
              if (!isEmpty(mibCodeDetails)) {
                orders.push(
                  new EvidenceOrder(TaskTypes.MIB_CODE_DETAILS, mibCodeDetails),
                );
              }
              if (!isEmpty(insuranceLabs)) {
                orders.push(
                  new EvidenceOrder(TaskTypes.INSURANCE_LABS, insuranceLabs),
                );
              }
              if (!isEmpty(criminalRecords)) {
                orders.push(
                  new EvidenceOrder(
                    TaskTypes.CRIMINAL_RECORDS,
                    criminalRecords,
                  ),
                );
              }

              if (!isEmpty(additionalCustomerInfo)) {
                orders.push(
                  new EvidenceOrder(
                    TaskTypes.ADDITIONAL_INFORMATION_FROM_CUSTOMER,
                    additionalCustomerInfo,
                  ),
                );
              }

              if (!isEmpty(specialAuthorization)) {
                orders.push(
                  new EvidenceOrder(
                    TaskTypes.SPECIAL_AUTHORIZATION,
                    specialAuthorization,
                  ),
                );
              }
              if (!isEmpty(planF)) {
                orders.push(new EvidenceOrder(TaskTypes.PLAN_F, planF));
              }
              if (!isEmpty(autoMedicalClaims)) {
                orders.push(
                  new EvidenceOrder(
                    TaskTypes.AUTO_MEDICAL_CLAIMS,
                    autoMedicalClaims,
                  ),
                );
              }
              if (!isEmpty(trlOrder)) {
                orders.push(new EvidenceOrder(TaskTypes.TRL, trlOrder));
              }
              if (!isEmpty(dispute)) {
                orders.push(
                  new EvidenceOrder(TaskTypes.DISPUTE, dispute as any),
                );
              }
              if (!isEmpty(additionalDocuments)) {
                orders.push(
                  new EvidenceOrder(
                    TaskTypes.ADDITIONAL_DOCUMENTS,
                    additionalDocuments,
                  ),
                );
              }
              dispatch(
                placeOrder(applicationId, orders, openSnacks, updateSnacks),
              );
              setIsReview(false);
              setIsOpen(false);
            }}
          >
            Submit Order
          </NoraButton>
        </Box>
      </Box>
    </Drawer>
  );
};
